import {
  MouseEvent,
  ReactNode,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { useNotifications } from 'src/contexts/NotificationContext';
import { getNotificationSourceValue } from 'src/utils/notificationFields';
import { groupRecertTrackingNotifications } from 'src/utils/notificationRecertGrouping';
import MenuPopover from 'src/components/MenuPopover';
import NotificationRow from 'src/components/Notifications/NotificationRow';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import { Notification } from 'src/types/Notifications';

export interface RenterNotificationIconHandle {
  handleClick: (event: MouseEvent<HTMLElement>) => void;
}

interface RenterNotificationIconProps {
  iconSize?: number;
  ref?: Ref<RenterNotificationIconHandle>;
  iconColor?: string;
}

const RenterNotificationIcon = ({
  iconSize = 20,
  ref,
  iconColor,
}: RenterNotificationIconProps): ReactNode => {
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    loading,
    error,
    markAsRead,
    bulkMarkAsRead,
    markAsUnread,
    markAllAsRead,
  } = useNotifications();

  const renterInAppPreference = user?.receive_renter_notifications;
  const inAppDisabled = renterInAppPreference === false;

  const visibleNotifications = useMemo(() => {
    const filtered = notifications.filter(
      (notification) => getNotificationSourceValue(notification) === 'renter'
    );
    return groupRecertTrackingNotifications(filtered);
  }, [notifications]);

  const visibleUnreadCount = useMemo(() => {
    return visibleNotifications.filter((notification) => !notification.is_read)
      .length;
  }, [visibleNotifications]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (inAppDisabled) {
        return;
      }
      event.stopPropagation();
      setAnchorEl((prev) => (prev ? null : event.currentTarget));
    },
    [inAppDisabled]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useImperativeHandle(ref, () => ({
    handleClick: handleClick,
  }));

  const handleNotificationClick = useCallback(
    async (notif: Notification): Promise<void> => {
      const groupedIds = notif.related_objects?._groupedIds;
      if (groupedIds?.length) {
        if (!notif.is_read) {
          try {
            await bulkMarkAsRead(groupedIds);
          } catch {
            enqueueSnackbar('Failed to mark notifications as read.', {
              variant: 'error',
            });
          }
        }
      } else if (!notif.is_read) {
        try {
          await markAsRead(notif.id);
        } catch {
          enqueueSnackbar('Failed to mark notification as read.', {
            variant: 'error',
          });
        }
      }
      handleClose();
      navigate(`/${RENTER_PATHS.HOME}/${RENTER_PATHS.NOTIFICATIONS}`);
    },
    [bulkMarkAsRead, markAsRead, enqueueSnackbar, handleClose, navigate]
  );

  const handleMarkAllRead = useCallback(async (): Promise<void> => {
    try {
      await markAllAsRead();
    } catch {
      enqueueSnackbar('Failed to mark all as read.', { variant: 'error' });
    }
  }, [markAllAsRead, enqueueSnackbar]);

  const handleMarkAsRead = useCallback(
    async (notifId: number, e: MouseEvent): Promise<void> => {
      e.stopPropagation();
      try {
        await markAsRead(notifId);
      } catch {
        enqueueSnackbar('Failed to mark notification as read.', {
          variant: 'error',
        });
      }
    },
    [markAsRead, enqueueSnackbar]
  );

  const handleMarkAsUnread = useCallback(
    async (notifId: number, e: MouseEvent): Promise<void> => {
      e.stopPropagation();
      try {
        await markAsUnread(notifId);
      } catch {
        enqueueSnackbar('Failed to mark notification as unread.', {
          variant: 'error',
        });
      }
    },
    [markAsUnread, enqueueSnackbar]
  );

  const handleSeeAll = useCallback(() => {
    handleClose();
    navigate(`/${RENTER_PATHS.HOME}/${RENTER_PATHS.NOTIFICATIONS}`);
  }, [handleClose, navigate]);

  const isOpen = Boolean(anchorEl);

  return (
    <>
      <Box
        ref={iconRef}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: inAppDisabled ? 'default' : 'pointer',
          opacity: inAppDisabled ? 0.5 : 1,
        }}
      >
        <Badge
          badgeContent={visibleUnreadCount > 0 ? visibleUnreadCount : null}
          color="error"
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': { boxShadow: 'none', outline: 'none' },
          }}
        >
          {visibleUnreadCount > 0 ? (
            <NotificationsActiveIcon
              sx={{ fontSize: iconSize, color: iconColor ?? 'primary.main' }}
            />
          ) : (
            <NotificationsIcon
              sx={{
                fontSize: iconSize,
                color:
                  iconColor ??
                  (inAppDisabled ? 'action.disabled' : 'action.active'),
              }}
            />
          )}
        </Badge>
      </Box>
      <MenuPopover
        open={isOpen}
        onClose={handleClose}
        anchorEl={iconRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          width: 360,
          '& .arrow': {
            left: ' 5px',
          },
        }}
        container={document.body}
      >
        <Box
          sx={{
            p: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1">Notifications</Typography>
          <Button
            size="small"
            onClick={handleMarkAllRead}
            disabled={visibleUnreadCount === 0}
          >
            Mark all as read
          </Button>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : visibleUnreadCount === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsIcon
              sx={{
                fontSize: 48,
                color: 'text.secondary',
                mb: 1,
                opacity: 0.6,
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              You&apos;re all caught up!
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                opacity: 0.8,
              }}
            >
              No unread notifications at the moment.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {visibleNotifications
              .filter((notif) => !notif.is_read)
              .sort(
                (a, b) =>
                  new Date(b.created).getTime() - new Date(a.created).getTime()
              )
              .slice(0, 5)
              .map((notif) => (
                <NotificationRow
                  key={notif.id}
                  notification={notif}
                  onNotificationClick={handleNotificationClick}
                  onMarkAsRead={handleMarkAsRead}
                  onMarkAsUnread={handleMarkAsUnread}
                  showTypeIcon={true}
                  showTimestamps={true}
                  isDropdown={true}
                  newNotificationIds={new Set()}
                />
              ))}
          </List>
        )}
        <Divider sx={{ mt: 1, mb: 0.5 }} />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button
            size="small"
            color="primary"
            onClick={handleSeeAll}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            See All Notifications
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
};

export default RenterNotificationIcon;
